import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useParams, useRouter, useRouterState } from '@tanstack/react-router';

/**
 * The part of the address below the page that is showing.
 *
 * Addresses are paths, not query strings (14 September 2026):
 * /manage/meridian/extensions/1001/phone, where there was
 * /manage/meridian?section=extensions&ext=<uuid>&view=phone. The owner does not
 * want long URLs anywhere on the platform, and a support link is read, pasted
 * and said out loud.
 *
 * Each page reads the segments below itself and hands the rest down, so a page
 * embedded in several places -- Extensions sits inside an account in both
 * consoles, and on its own at /my/extensions -- works out its own address
 * without knowing where it is mounted.
 */
export interface Subroute {
  /** The segments below this page, decoded. */
  rest: string[];
  /** Points the address at these segments below this page. */
  go: (rest: string[], opts?: { replace?: boolean }) => void;
  /** The address of these segments below this page, for a real link (ctrl-click, copy link). */
  href: (rest: string[]) => string;
}

const SubrouteContext = createContext<Subroute | null>(null);

/** Below a splat route (`/manage/$tenantId/$`): its `_splat`, under whatever the route's own path matched. */
function useRouteSubroute(): Subroute {
  const router = useRouter();
  const { _splat } = useParams({ strict: false }) as { _splat?: string };
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return useMemo(() => {
    const splat = (_splat ?? '').replace(/^\/+|\/+$/g, '');
    const path = pathname.replace(/\/+$/, '');
    const base = splat && path.endsWith(`/${splat}`) ? path.slice(0, -(splat.length + 1)) : path;
    return {
      rest: splat ? splat.split('/').filter(Boolean).map(decodeURIComponent) : [],
      go: (next, opts) => {
        const to = [base, ...next.map(encodeURIComponent)].join('/') || '/';
        if (opts?.replace) router.history.replace(to); else router.history.push(to);
      },
      href: (next) => [base, ...next.map(encodeURIComponent)].join('/') || '/',
    };
  }, [_splat, pathname, router]);
}

/** The address below the current page: from the nearest page that handed one down, else from the route. */
export function useSubroute(): Subroute {
  const fromRoute = useRouteSubroute();
  return useContext(SubrouteContext) ?? fromRoute;
}

/** What a page hands down to what it renders: everything below `head`, the segments it has used. */
export function childSubroute(parent: Subroute, head: string[]): Subroute {
  return {
    rest: parent.rest.slice(head.length),
    go: (next, opts) => parent.go([...head, ...next], opts),
    href: (next) => parent.href([...head, ...next]),
  };
}

/**
 * The account's own address, for links from one section to another ("IVR menu · Main menu" on a
 * route, a number's route on Assigned numbers). Set by the account console around its sections.
 */
const AccountRouteContext = createContext<Subroute | null>(null);
export const AccountRouteProvider = AccountRouteContext.Provider;
export const useAccountRoute = (): Subroute | null => useContext(AccountRouteContext);

export function SubrouteProvider({ value, children }: { value: Subroute; children: ReactNode }) {
  return <SubrouteContext.Provider value={value}>{children}</SubrouteContext.Provider>;
}
